import { ErrorListResponse, Service, instanceOfErrorListResponse } from '@genial/services';
import { useCallback } from 'react';
import { useSetRecoilState } from 'recoil';

import { useToastCreator } from '../../hooks/useToastCreator';
import { successRateManagementSentMessage } from '../../pages/RateManagement/constants';
import { exportPdf } from '../../pages/RateManagement/RequestDetails/exportPdf';

import {
  rateManagementCancelOrderState,
  rateManagementCancelledOrderState,
  rateManagementDownloadSpreadsheetState,
  rateManagementSendOrderState,
  rateManagementSentOrderState,
  rateManagementUploadState,
  rateManagementUploadedOrderState,
} from './atom';
import { screenName } from './constants';
import { SentOrderDetailResponseNormalized } from './selector';

export const useRateManagementActions = () => {
  const setRateManagementUploadState = useSetRecoilState(rateManagementUploadState);
  const setRateManagementCancelOrderState = useSetRecoilState(rateManagementCancelOrderState);
  const setRateManagementSendOrderState = useSetRecoilState(rateManagementSendOrderState);
  const setRateManagementCancelledOrderState = useSetRecoilState(rateManagementCancelledOrderState);
  const setRateManagementSentOrderState = useSetRecoilState(rateManagementSentOrderState);
  const setRateManagementUploadedOrderState = useSetRecoilState(rateManagementUploadedOrderState);
  const setRateManagementDownloadSpreadsheetState = useSetRecoilState(
    rateManagementDownloadSpreadsheetState
  );
  const toast = useToastCreator('rate-management-upload');

  const showToastForBackendError = useCallback(
    (e: any) => {
      if (instanceOfErrorListResponse(e)) {
        const backendError = e as ErrorListResponse;
        const errorMessage = backendError.errors?.[0];

        if (errorMessage) {
          toast.showToastFromError(errorMessage);
        }
      }
    },
    [toast]
  );

  const uploadSpreadsheet = useCallback(
    (file: File, competence: string) => {
      setRateManagementUploadState({
        isUploadFailed: false,
        isUploaded: false,
        isUploading: true,
      });

      const handleError = (e: unknown) => {
        showToastForBackendError(e);

        setRateManagementUploadState({
          isUploadFailed: true,
          isUploaded: false,
          isUploading: false,
        });
      };

      Service.ManagementFee.V1.postFilesUpload({
        screenName,
        managementFeeFileUploadRequest: {
          managementFeeCompetence: competence,
        },
      })
        .then((response) => {
          const signedPutUrl = response.data?.preSignedUrl;

          if (signedPutUrl) {
            fetch(signedPutUrl, {
              method: 'PUT',
              body: file,
              mode: 'cors',
              credentials: 'same-origin',
            })
              .then(() => {
                setRateManagementUploadState({
                  isUploadFailed: false,
                  isUploaded: true,
                  isUploading: false,
                });
              })
              .catch(handleError);
          }
        })
        .catch(handleError);
    },
    [setRateManagementUploadState, showToastForBackendError]
  );

  const cancelOrder = useCallback(
    (orderId: string | number) => {
      setRateManagementCancelOrderState({
        isCancelFailed: false,
        isCanceled: false,
        isCanceling: true,
      });

      const handleError = (e: unknown) => {
        showToastForBackendError(e);

        setRateManagementCancelOrderState({
          isCancelFailed: true,
          isCanceled: false,
          isCanceling: false,
        });
      };

      Service.ManagementFee.V1.patchOrdersByCancel({
        orderId: orderId.toString(),
        screenName,
      })
        .then(() => {
          setRateManagementCancelOrderState({
            isCancelFailed: false,
            isCanceled: true,
            isCanceling: false,
          });
        })
        .catch(handleError);
    },
    [setRateManagementCancelOrderState, showToastForBackendError]
  );

  const sendOrder = useCallback(
    (orderId: string | number, managerAccountNumber: number, managerAccountDigit: string) => {
      setRateManagementSendOrderState({
        isSendFailed: false,
        isSent: false,
        isSending: true,
      });

      const handleError = (e: unknown) => {
        showToastForBackendError(e);

        setRateManagementSendOrderState({
          isSendFailed: true,
          isSent: false,
          isSending: false,
        });
      };

      Service.ManagementFee.V1.putOrdersSend({
        managementFeeSendOrderRequest: {
          orderId: orderId.toString(),
          managerAccountNumber,
          managerAccountDigit,
        },
        screenName,
      })
        .then(() => {
          setRateManagementSendOrderState({
            isSendFailed: false,
            isSent: true,
            isSending: false,
          });
          toast.showToast(...successRateManagementSentMessage);
        })
        .catch(handleError);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setRateManagementSendOrderState, showToastForBackendError]
  );

  const exportOrderDetailsPdf = useCallback((orderData: SentOrderDetailResponseNormalized) => {
    exportPdf(orderData);
  }, []);

  const downloadSpreadsheet = useCallback(() => {
    setRateManagementDownloadSpreadsheetState({
      isDownloadFailed: false,
      isDownloaded: false,
      isDownloading: true,
    });

    const handleError = (e: unknown) => {
      showToastForBackendError(e);

      setRateManagementDownloadSpreadsheetState({
        isDownloadFailed: true,
        isDownloaded: false,
        isDownloading: false,
      });
    };

    Service.ManagementFee.V1.getFilesDownload({
      screenName,
    })
      .then((response) => {
        const signedGetUrl = response.data?.preSignedUrl;

        if (signedGetUrl) {
          window.open(signedGetUrl, '_blank');
        }
      })
      .catch(handleError);
  }, [setRateManagementDownloadSpreadsheetState, showToastForBackendError]);

  const notifyOrderCancelled = useCallback(() => {
    setRateManagementCancelledOrderState({
      wasCancelled: true,
    });
  }, [setRateManagementCancelledOrderState]);

  const notifyOrderSent = useCallback(() => {
    setRateManagementSentOrderState({
      wasSent: true,
    });
  }, [setRateManagementSentOrderState]);

  const notifyOrderUploaded = useCallback(() => {
    setRateManagementUploadedOrderState({
      wasUploaded: true,
    });
  }, [setRateManagementUploadedOrderState]);

  const resetUploadState = useCallback(
    () =>
      setRateManagementUploadState({
        isUploaded: false,
        isUploadFailed: false,
        isUploading: false,
      }),
    [setRateManagementUploadState]
  );

  const resetCancelOrderState = useCallback(() => {
    setRateManagementCancelOrderState({
      isCanceling: false,
      isCanceled: false,
      isCancelFailed: false,
    });
  }, [setRateManagementCancelOrderState]);

  const resetSendOrderState = useCallback(() => {
    setRateManagementSendOrderState({
      isSending: false,
      isSent: false,
      isSendFailed: false,
    });
  }, [setRateManagementSendOrderState]);

  const resetNotifyOrderCancelled = useCallback(() => {
    setRateManagementCancelledOrderState({
      wasCancelled: false,
    });
  }, [setRateManagementCancelledOrderState]);

  const resetNotifyOrderSent = useCallback(() => {
    setRateManagementSentOrderState({
      wasSent: false,
    });
  }, [setRateManagementSentOrderState]);

  const resetNotifyOrderUploaded = useCallback(() => {
    setRateManagementUploadedOrderState({
      wasUploaded: false,
    });
  }, [setRateManagementUploadedOrderState]);

  return {
    cancelOrder,
    exportOrderDetailsPdf,
    notifyOrderCancelled,
    notifyOrderSent,
    notifyOrderUploaded,
    sendOrder,
    downloadSpreadsheet,
    resetUploadState,
    resetCancelOrderState,
    resetSendOrderState,
    resetNotifyOrderCancelled,
    resetNotifyOrderSent,
    resetNotifyOrderUploaded,
    uploadSpreadsheet,
  };
};
